import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { CoachFeedback } from '@/components/CoachFeedback';

interface TrueFalseQuestionProps {
  question: string;
  correctAnswer: boolean;
  explanation: string;
  onAnswer: (isCorrect: boolean) => void;
  answered: boolean;
}

export function TrueFalseQuestion({ question, correctAnswer, explanation, onAnswer, answered }: TrueFalseQuestionProps) {
  const [selected, setSelected] = useState<boolean | null>(null);
  const isCorrect = selected === correctAnswer;

  const handleSelect = useCallback((value: boolean) => {
    if (selected !== null) return;
    setSelected(value);
    onAnswer(value === correctAnswer);
  }, [selected, correctAnswer, onAnswer]);

  return (
    <div className="w-full space-y-5">
      <div className="text-center">
        <span className="inline-block bg-primary/10 text-primary font-display font-bold text-xs px-3 py-1 rounded-full mb-3">
          ✅❌ TRUE OR FALSE
        </span>
      </div>

      {/* Statement card */}
      <motion.div
        className="bg-card rounded-2xl border-2 border-border p-6 shadow-card"
        animate={selected !== null ? (isCorrect ? { borderColor: 'hsl(142, 71%, 45%)' } : { borderColor: 'hsl(0, 84%, 60%)' }) : {}}
      >
        <p className="font-body font-semibold text-lg text-center leading-relaxed">
          "{question}"
        </p>
      </motion.div>

      {/* True / False buttons */}
      <div className="grid grid-cols-2 gap-3">
        <motion.button
          onClick={() => handleSelect(true)}
          disabled={selected !== null}
          whileTap={selected === null ? { scale: 0.95 } : {}}
          animate={
            selected !== null
              ? selected === true
                ? isCorrect
                  ? { scale: [1, 1.05, 1] }
                  : { x: [0, -6, 6, -4, 4, 0] }
                : correctAnswer === true
                  ? {}
                  : {}
              : {}
          }
          className={`h-16 rounded-xl border-2 font-display font-bold text-lg flex items-center justify-center gap-2 transition-all ${
            selected === null
              ? 'bg-success/5 border-success/30 text-success hover:bg-success/15 hover:border-success'
              : selected === true
                ? isCorrect
                  ? 'bg-success/20 border-success text-success ring-2 ring-success/30'
                  : 'bg-destructive/20 border-destructive text-destructive ring-2 ring-destructive/30'
                : correctAnswer === true
                  ? 'bg-success/10 border-success/50 text-success'
                  : 'bg-muted/50 border-border opacity-50 text-muted-foreground'
          }`}
        >
          <Check className="w-5 h-5" />
          TRUE
        </motion.button>

        <motion.button
          onClick={() => handleSelect(false)}
          disabled={selected !== null}
          whileTap={selected === null ? { scale: 0.95 } : {}}
          animate={
            selected !== null
              ? selected === false
                ? isCorrect
                  ? { scale: [1, 1.05, 1] }
                  : { x: [0, -6, 6, -4, 4, 0] }
                : correctAnswer === false
                  ? {}
                  : {}
              : {}
          }
          className={`h-16 rounded-xl border-2 font-display font-bold text-lg flex items-center justify-center gap-2 transition-all ${
            selected === null
              ? 'bg-destructive/5 border-destructive/30 text-destructive hover:bg-destructive/15 hover:border-destructive'
              : selected === false
                ? isCorrect
                  ? 'bg-success/20 border-success text-success ring-2 ring-success/30'
                  : 'bg-destructive/20 border-destructive text-destructive ring-2 ring-destructive/30'
                : correctAnswer === false
                  ? 'bg-success/10 border-success/50 text-success'
                  : 'bg-muted/50 border-border opacity-50 text-muted-foreground'
          }`}
        >
          <X className="w-5 h-5" />
          FALSE
        </motion.button>
      </div>

      {/* Result indicator */}
      {selected !== null && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <span className={`font-display font-bold text-sm ${isCorrect ? 'text-success' : 'text-destructive'}`}>
            {isCorrect ? '✅ Correct!' : `❌ The answer is ${correctAnswer ? 'TRUE' : 'FALSE'}`}
          </span>
        </motion.div>
      )}
    </div>
  );
}
